
import { lazy, Suspense } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import HomePage from '../pages/home/page';
import SearchPage from '../pages/search/page';
import MapPage from '../pages/map/page';
import BrokersMapPage from '../pages/brokers-map/page';
import PropertyPage from '../pages/property/page';
import CreatePropertyPage from '../pages/property/create/page';
import EditPropertyPage from '../pages/property/edit/page';
import ContactPage from '../pages/contact/page';
import SignInPage from '../pages/auth/signin/page';
import ResetPasswordPage from '../pages/auth/reset-password/page';
import RegisterPage from '../pages/register/page';
import RegistrationSuccessPage from '../pages/register/success/page';
import ProfilePage from '../pages/profile/page';
import AccountPage from '../pages/account/page';
import VerifyIdentityPage from '../pages/account/verify-identity/page';
import ProfessionalAccountPage from '../pages/account/professional/page';
import BillingPage from '../pages/billing/page';
import AdvertisementsPage from '../pages/advertisements/page';
import ServiceEngagementsPage from '../pages/service-engagements/page';
import SpecialDealsPage from '../pages/special-deals/page';
import ProjectsNonSaudisPage from '../pages/projects-non-saudis/page';
import SettingsPage from '../pages/settings/page';
import TermsPage from '../pages/terms/page';
import PrivacyPage from '../pages/privacy/page';
import AboutPage from '../pages/about/page';
import NotFoundPage from '../pages/NotFound';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import ProfessionalRoute from '../components/auth/ProfessionalRoute';
import ProtectedCompanyRoute from '../components/auth/ProtectedCompanyRoute';
import MembershipPage from '../pages/membership/page';
import MembershipSuccessPage from '../pages/membership/success/page';
import AdvertiserRoute from '../components/auth/AdvertiserRoute';
import FavoritesPage from '../pages/favorites/page';
import ListDetailPage from '../pages/favorites/[listId]/page';
import MyListingsPage from '../pages/listings/page';
import EnquiriesPage from '../pages/enquiries/page';
import AdminDashboardPage from '../pages/admin/page';
import AdminUsersPage from '../pages/admin/users/page';
import AdminDevelopersPage from '../pages/admin/developers/page';
import AdminDeveloperDetailPage from '../pages/admin/developers/detail';
import AdminInvestorLeadsPage from '../pages/admin/investor-leads/page';
import AdminTiersPage from '../pages/admin/subscriptions/tiers/page';
import AdminPromoCodesPage from '../pages/admin/subscriptions/promo-codes/page';
import AdminAnalyticsPage from '../pages/admin/subscriptions/analytics/page';
import AdminUserSubscriptionsPage from '../pages/admin/subscriptions/users/page';
import AdminAddonsPage from '../pages/admin/subscriptions/addons/page';
import AdminAddonOrdersPage from '../pages/admin/subscriptions/addon-orders/page';
import AdminBundlesPage from '../pages/admin/subscriptions/bundles/page';
import AddonsStorefrontPage from '../pages/addons/page';
import BundlesStorefrontPage from '../pages/bundles/page';
import MyAddonsPage from '../pages/my-addons/page';
import CoinsPage from '../pages/coins/page';
import AdminCoinsPage from '../pages/admin/subscriptions/coins/page';
import AdminNeighborhoodsPage from '../pages/admin/neighborhoods/page';
import AdminNeighborhoodDetailPage from '../pages/admin/neighborhoods/detail';
import AdminInspectionsPage from '../pages/admin/inspections/page';
import JoinTeamPage from '../pages/join-team/page';
import DashboardPage from '../pages/dashboard/page';
import StatisticsPage from '../pages/dashboard/statistics/page';
import TeamPage from '../pages/dashboard/team/page';
import CompanyTeamPage from '../pages/company/TeamPage';
import TrustedBrokersPage from '../pages/trusted-brokers/page';
import BrokerProfilePage from '../pages/broker/page';
import CompanyProfilePage from '../pages/companies/page';
import DeveloperProfilePage from '../pages/developers/page';
import DeveloperProjectPage from '../pages/developers/project';
import InvestorsHubPage from '../pages/investors/page';
import LicensedProjectsPage from '../pages/investors/licensed-projects';
import ContributionsPage from '../pages/investors/contributions';
import InvestmentRequestsPage from '../pages/investors/requests';
import ListForInvestmentPage from '../pages/investors/list';
import ProfessionalsDirectoryPage from '../pages/professionals/page';
import ProfessionalProfilePage from '../pages/professionals/profile';
import ProtectedDeveloperRoute from '../components/auth/ProtectedDeveloperRoute';
import DeveloperDashboardPage from '../pages/developer/DashboardPage';
import DeveloperTeamPage from '../pages/developer/TeamPage';
import DeveloperSettingsPage from '../pages/developer/SettingsPage';
import DeveloperProjectsPage from '../pages/developer/projects/page';
import CreateClientRequest from '../pages/client-requests/CreateClientRequest';
import ClientRequestSuccess from '../pages/client-requests/ClientRequestSuccess';
import BrokerRequestsList from '../pages/client-requests/BrokerRequestsList';
// AuthorizationWizard retired — /authorization/new now redirects to the unified
// /individual-property-request wizard. AuthorizationSuccess kept for its route.
import AuthorizationSuccess from '../pages/authorization/AuthorizationSuccess';
import AcceptInvitationPage from '../pages/AcceptInvitationPage';
import CompanyDashboardPage from '../pages/company/DashboardPage';
import CompanySettingsPage from '../pages/company/SettingsPage';
import CompanyStatisticsPage from '../pages/company/StatisticsPage';
import MyPropertyRequestsPage from '../pages/my-property-requests/page';
import ComplaintsPage from '../pages/complaints/page';
import MyComplaintsPage from '../pages/complaints/my/page';
import ComplaintDetailPage from '../pages/complaints/my/[id]/page';
import AdminComplaintsPage from '../pages/admin/complaints/page';
import SetupTwoFactorPage from '../pages/admin/setup-2fa/page';
import AdminGuard from '../components/admin/AdminGuard';
import FALDisclaimerPage from '../pages/fal-disclaimer/page';
import IndividualPropertyRequestPage from '../pages/individual-property-request/page';
import MarketInsightsPage from '../pages/market-insights/page';       // Phase 7A (public, gated)
// The market console is the one lazily-loaded route in the app. It carries 17 instruments
// across five engine surfaces and pulls in the whole market-ui kit; eagerly importing it
// put all of that in the main bundle for every visitor who never opens it.
const MarketToolsPage = lazy(() => import('../pages/market-tools/page'));
import MyInsightsPage from '../pages/account/insights/page';          // Phase 7B/7C (self-service, gated)
import IconGalleryPage from '../pages/design/icons/page';             // unlisted QA reference
import FaqPage from '../pages/faq/page';

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />
  },
  {
    path: '/market-insights',
    element: <MarketInsightsPage />
  },
  {
    path: '/market-tools',
    // A plain white fallback rather than a spinner: the page paints its own skeleton the
    // moment it mounts, and two loading treatments in a row reads as a stutter.
    element: (
      <Suspense fallback={<div className="min-h-screen bg-white" />}>
        <MarketToolsPage />
      </Suspense>
    )
  },
  {
    path: '/account/insights',
    element: (
      <ProtectedRoute>
        <MyInsightsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/search',
    element: <SearchPage />
  },
  {
    // Unlisted design reference: the unified domain icon catalog (QA aid).
    path: '/design/icons',
    element: <IconGalleryPage />
  },
  {
    path: '/map',
    element: <MapPage />
  },
  {
    path: '/brokers/map',
    element: <BrokersMapPage />
  },
  {
    path: '/property/:id',
    element: <PropertyPage />
  },
  {
    path: '/property/:id/edit',
    element: (
      <ProtectedRoute requireAuth={true} requireBroker={true}>
        <EditPropertyPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/properties/new',
    element: (
      <ProtectedRoute requireAuth={true} requireBroker={true}>
        <CreatePropertyPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/contact',
    element: <ContactPage />
  },
  {
    path: '/signin',
    element: (
      <ProtectedRoute requireAuth={false}>
        <SignInPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/register',
    element: <RegisterPage />
  },
  {
    path: '/register/success',
    element: <RegistrationSuccessPage />
  },
  {
    path: '/register-type',
    element: <Navigate to="/register" replace />
  },
  {
    path: '/signup',
    element: <Navigate to="/register" replace />
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute requireAuth={true}>
        <ProfilePage />
      </ProtectedRoute>
    )
  },
  {
    path: '/account',
    element: (
      <ProtectedRoute requireAuth={true}>
        <AccountPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/account/verify-identity',
    element: (
      <ProtectedRoute requireAuth={true}>
        <VerifyIdentityPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/account/professional',
    element: (
      <ProtectedRoute requireAuth={true}>
        <ProfessionalAccountPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/billing',
    element: (
      <ProtectedRoute requireAuth={true}>
        <BillingPage />
      </ProtectedRoute>
    )
  },
  {
    // «لوحة الطلبات» is for professional accounts only (owner, 2026-10). Guests go
    // to /signin; signed-in non-professionals see a locked card. The API enforces
    // the same rule on every call (requireProfessional → 403 PROFESSIONAL_ACCOUNT_REQUIRED).
    path: '/advertisements',
    element: (
      <ProfessionalRoute>
        <AdvertisementsPage />
      </ProfessionalRoute>
    )
  },
  {
    // Public: a guest sees the five services and how a request moves, with a
    // sign-in prompt; a signed-in user sees their requests. Every API call
    // still requires sign-in.
    path: '/service-engagements',
    element: <ServiceEngagementsPage />
  },
  {
    path: '/special-deals',
    element: <SpecialDealsPage />
  },
  {
    path: '/projects-non-saudis',
    element: <ProjectsNonSaudisPage />
  },
  {
    path: '/settings',
    element: <SettingsPage />
  },
  {
    path: '/membership',
    // Advertiser subscriptions are for brokers/companies; logged-in individuals
    // are redirected to /addons (they list one-off under the platform FAL).
    element: (
      <AdvertiserRoute>
        <MembershipPage />
      </AdvertiserRoute>
    )
  },
  {
    path: '/membership/success',
    element: <MembershipSuccessPage />
  },
  {
    path: '/addons',
    element: <AddonsStorefrontPage />
  },
  {
    path: '/bundles',
    element: <BundlesStorefrontPage />
  },
  {
    // Public like /addons — the page itself shows a sign-in prompt when logged
    // out (no per-user data is fetched until authenticated).
    path: '/coins',
    element: <CoinsPage />
  },
  {
    // Public like /addons — the page shows a sign-in prompt when logged out.
    path: '/my-addons',
    element: <MyAddonsPage />
  },
  {
    path: '/favorites',
    element: (
      <ProtectedRoute requireAuth={true}>
        <FavoritesPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/favorites/:listId',
    element: (
      <ProtectedRoute requireAuth={true}>
        <ListDetailPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/listings',
    element: (
      <ProtectedRoute requireAuth={true}>
        <MyListingsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/enquiries',
    element: (
      <ProtectedRoute requireAuth={true}>
        <EnquiriesPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute requireAuth={true}>
        <DashboardPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/dashboard/statistics',
    element: (
      <ProtectedRoute requireAuth={true}>
        <StatisticsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/dashboard/team',
    element: (
      <ProtectedRoute requireAuth={true}>
        <TeamPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/admin',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminDashboardPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/users',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminUsersPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/developers',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminDevelopersPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/developers/:id',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminDeveloperDetailPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/investor-leads',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminInvestorLeadsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/tiers',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminTiersPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/promo-codes',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminPromoCodesPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/analytics',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminAnalyticsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/users',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminUserSubscriptionsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/addons',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminAddonsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/addon-orders',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminAddonOrdersPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/bundles',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminBundlesPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/subscriptions/coins',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminCoinsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/neighborhoods',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminNeighborhoodsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/neighborhoods/:areaId',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminNeighborhoodDetailPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/inspections',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminInspectionsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/company/dashboard',
    element: (
      <ProtectedCompanyRoute requiredRole="AGENT">
        <CompanyDashboardPage />
      </ProtectedCompanyRoute>
    )
  },
  {
    path: '/company/team',
    element: (
      <ProtectedCompanyRoute requiredRole="ADMIN">
        <CompanyTeamPage />
      </ProtectedCompanyRoute>
    )
  },
  {
    path: '/company/statistics',
    element: (
      <ProtectedCompanyRoute requiredRole="ADMIN">
        <CompanyStatisticsPage />
      </ProtectedCompanyRoute>
    )
  },
  {
    path: '/company/settings',
    element: (
      <ProtectedCompanyRoute requiredRole="AGENT">
        <CompanySettingsPage />
      </ProtectedCompanyRoute>
    )
  },
  {
    path: '/join-team/:token',
    element: <JoinTeamPage />
  },
  {
    path: '/trusted-brokers',
    element: <TrustedBrokersPage />
  },
  {
    path: '/professionals',
    element: <ProfessionalsDirectoryPage />
  },
  {
    path: '/professionals/:id',
    element: <ProfessionalProfilePage />
  },
  {
    path: '/broker/:id',
    element: <BrokerProfilePage />
  },
  {
    path: '/companies/:slug',
    element: <CompanyProfilePage />
  },
  {
    path: '/developers/project/:projectSlug',
    element: <DeveloperProjectPage />
  },
  {
    path: '/developers/:slug',
    element: <DeveloperProfilePage />
  },
  {
    path: '/investors',
    element: <InvestorsHubPage />
  },
  {
    path: '/investors/licensed-projects',
    element: <LicensedProjectsPage />
  },
  {
    path: '/investors/contributions',
    element: <ContributionsPage />
  },
  {
    path: '/investors/requests',
    element: <InvestmentRequestsPage />
  },
  {
    path: '/investors/list',
    element: <ListForInvestmentPage />
  },
  {
    path: '/developer/dashboard',
    element: (
      <ProtectedDeveloperRoute requiredRole="AGENT">
        <DeveloperDashboardPage />
      </ProtectedDeveloperRoute>
    )
  },
  {
    path: '/developer/projects',
    element: (
      <ProtectedDeveloperRoute requiredRole="AGENT">
        <DeveloperProjectsPage />
      </ProtectedDeveloperRoute>
    )
  },
  {
    path: '/developer/team',
    element: (
      <ProtectedDeveloperRoute requiredRole="AGENT">
        <DeveloperTeamPage />
      </ProtectedDeveloperRoute>
    )
  },
  {
    path: '/developer/settings',
    element: (
      <ProtectedDeveloperRoute requiredRole="AGENT">
        <DeveloperSettingsPage />
      </ProtectedDeveloperRoute>
    )
  },
  {
    path: '/client-requests/new',
    element: <CreateClientRequest />
  },
  {
    path: '/client-requests/success',
    element: <ClientRequestSuccess />
  },
  {
    path: '/my-requests',
    element: (
      <ProtectedRoute requireAuth={true}>
        <MyPropertyRequestsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/broker/requests',
    element: (
      <ProtectedRoute requireAuth={true}>
        <BrokerRequestsList />
      </ProtectedRoute>
    )
  },
  {
    // "تفويض عقار" was merged into the unified "أضف عقارك" wizard. Redirect any
    // bookmarks / old links to the unified individual-property-request flow.
    path: '/authorization/new',
    element: <Navigate to="/individual-property-request" replace />
  },
  {
    path: '/authorization/success',
    element: <AuthorizationSuccess />
  },
  {
    path: '/accept-invitation/:token',
    element: <AcceptInvitationPage />
  },
  {
    path: '/my-property-requests',
    element: (
      <ProtectedRoute requireAuth={true}>
        <MyPropertyRequestsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/fal-disclaimer',
    element: (
      <ProtectedRoute requireAuth={true}>
        <FALDisclaimerPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/individual-property-request',
    element: (
      <ProtectedRoute requireAuth={true}>
        <IndividualPropertyRequestPage />
      </ProtectedRoute>
    )
  },
  // Complaints routes
  {
    path: '/complaints',
    element: (
      <ProtectedRoute requireAuth={true}>
        <ComplaintsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/complaints/my',
    element: (
      <ProtectedRoute requireAuth={true}>
        <MyComplaintsPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/complaints/my/:id',
    element: (
      <ProtectedRoute requireAuth={true}>
        <ComplaintDetailPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/complaints',
    element: (
      <ProtectedRoute requireAuth={true} requireAdmin={true}>
        <AdminGuard>
          <AdminComplaintsPage />
        </AdminGuard>
      </ProtectedRoute>
    )
  },
  {
    path: '/admin/setup-2fa',
    element: (
      <ProtectedRoute requireAuth={true}>
        <SetupTwoFactorPage />
      </ProtectedRoute>
    )
  },
  {
    path: '/about',
    element: <AboutPage />
  },
  {
    path: '/faq',
    element: <FaqPage />
  },
  {
    path: '/terms',
    element: <TermsPage />
  },
  {
    path: '/privacy',
    element: <PrivacyPage />
  },
  {
    path: '/notifications',
    element: (
      <ProtectedRoute requireAuth={true}>
        <NotFoundPage />
      </ProtectedRoute>
    )
  },
  {
    path: '*',
    element: <NotFoundPage />
  }
];

export default routes;
